import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import { colors, radius, shadows } from '../../styles/theme';
import { formatTRY, formatSayi } from '../../utils/analytics/format';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const KATEGORI_GRUP = {
  YARI_MAMUL: { bg: '#fff3e0', fg: '#e65100', label: 'Yarı Mamul' },
  TABAK: { bg: '#e8eaf6', fg: '#3f51b5', label: 'Tabak' },
  HAMMADDE: { bg: '#e0f2f1', fg: '#00695c', label: 'Hammadde' },
  DIGER: { bg: '#f5f5f5', fg: '#666' },
};

const kategoriRozet = (kategori) => {
  if (!kategori) return null;
  const k = String(kategori).toLowerCase().trim();

  if ([
    'yari mamul', 'yarı mamul', 'urun', 'ara_urun', 'iç reçete / yarı mamul',
    'kasap ürünü', 'et üretim', 'i̇ç reçete / yarı mamul',
  ].includes(k)) {
    return KATEGORI_GRUP.YARI_MAMUL;
  }

  if (['mamul'].includes(k)) return KATEGORI_GRUP.TABAK;

  const hammaddeAltTipleri = [
    'hammadde', 'et', 'baharat', 'manav', 'market', 'ambalaj', 'sut_urun',
    'süt ürünleri', 'sos', 'ekmek', 'dana eti', 'kuzu eti', 'tavuk',
    'tavuk eti', 'sirke/sos', 'kuru gıda', 'tatlandırıcı', 'donuk/i̇şlenmiş',
    'donuk/işlenmiş', 'tursu_sirke', 'turşu', 'i̇çecek', 'içecek',
    'salata hazırlık', 'tatlı', 'tatlı hazırlık', 'yağlar', 'hayvansal yağ',
    'bakliyat/tahıl', 'fırın', 'karkas', 'süt ürünler', 'temizlik/sarf',
    'temizlik',
  ];

  if (hammaddeAltTipleri.includes(k)) {
    return { ...KATEGORI_GRUP.HAMMADDE, label: kategori };
  }
  return { ...KATEGORI_GRUP.DIGER, label: kategori };
};

const OzetKarti = ({ baslik, tutar, kayit, vurgu = false }) => (
  <div style={{
    flex: '1 1 220px',
    minWidth: 200,
    background: vurgu ? '#f8fafc' : colors.surface,
    border: `1px solid ${vurgu ? '#cbd5e1' : colors.border}`,
    borderRadius: radius.lg,
    padding: '16px 18px',
    boxShadow: shadows.md,
  }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: '#64748b',
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {baslik}
    </div>
    <div style={{ marginTop: 7, fontSize: 21, fontWeight: 800, color: '#1f2937' }}>
      {formatTRY(tutar)}
    </div>
    <div style={{ marginTop: 5, fontSize: 12, color: '#9ca3af' }}>
      {kayit} kayıt
    </div>
  </div>
);

const solBaslikStyle = {
  padding: '10px 9px', textAlign: 'left', fontWeight: 600,
  color: '#4b5563', whiteSpace: 'nowrap',
};

const sagBaslikStyle = {
  padding: '10px 9px', textAlign: 'right', fontWeight: 600,
  color: '#4b5563', whiteSpace: 'nowrap',
};

const RaporTablosu = ({
  baslik, aciklama, detay, toplamMaliyet,
  sortKey, sortDirection, handleSort,
}) => {
  const siraliDetay = [...detay].sort((a, b) => {
    if (!sortKey) return 0;
    const aValue = Number(a[sortKey] || 0);
    const bValue = Number(b[sortKey] || 0);
    return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
  });

  return (
    <div style={{
      background: colors.surface,
      borderRadius: radius.lg,
      boxShadow: shadows.md,
      overflow: 'hidden',
      marginBottom: 18,
    }}>
      <div style={{
        padding: '14px 16px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>
            {baslik}
          </div>
          <div style={{ marginTop: 3, fontSize: 12, color: '#9ca3af' }}>
            {detay.length} kayıt{aciklama ? ` • ${aciklama}` : ''}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Toplam Maliyet:{' '}
          <strong style={{ color: '#374151' }}>{formatTRY(toplamMaliyet)}</strong>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: `1px solid ${colors.border}` }}>
              <th style={solBaslikStyle}>Tarih</th>
              <th style={solBaslikStyle}>Lokasyon</th>
              <th style={solBaslikStyle}>Ürün</th>
              <th style={solBaslikStyle}>Birim</th>
              <th
                onClick={() => handleSort('adet')}
                style={{ ...sagBaslikStyle, cursor: 'pointer', userSelect: 'none' }}
              >
                Adet {sortKey === 'adet' ? (sortDirection === 'desc' ? '↓' : '↑') : '↕'}
              </th>
              <th style={sagBaslikStyle}>Birim Fiyat</th>
              <th
                onClick={() => handleSort('toplam_maliyet')}
                style={{ ...sagBaslikStyle, cursor: 'pointer', userSelect: 'none' }}
              >
                Toplam Maliyet{' '}
                {sortKey === 'toplam_maliyet' ? (sortDirection === 'desc' ? '↓' : '↑') : '↕'}
              </th>
            </tr>
          </thead>
          <tbody>
            {siraliDetay.map((d) => (
              <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '9px', whiteSpace: 'nowrap' }}>{d.tarih || '-'}</td>
                <td style={{ padding: '9px', whiteSpace: 'nowrap' }}>{d.lokasyon_ad || '-'}</td>
                <td style={{ padding: '9px', fontWeight: 500, minWidth: 220 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{d.urun_ad || '-'}</span>
                    {(() => {
                      const conf = kategoriRozet(d.malzeme_tipi);
                      return conf ? (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 6px',
                          borderRadius: 10, background: conf.bg, color: conf.fg,
                          whiteSpace: 'nowrap',
                        }}>
                          {conf.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </td>
                <td style={{ padding: '9px', whiteSpace: 'nowrap', color: '#64748b' }}>
                  {d.birim || '-'}
                </td>
                <td style={{ padding: '9px', textAlign: 'right', fontWeight: 600 }}>
                  {formatSayi(d.adet)}
                </td>
                <td style={{ padding: '9px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {formatTRY(d.birim_fiyat || 0, { decimals: 2 })}
                </td>
                <td style={{
                  padding: '9px', textAlign: 'right',
                  whiteSpace: 'nowrap', fontWeight: 600,
                }}>
                  {formatTRY(d.toplam_maliyet || 0, { decimals: 2 })}
                </td>
              </tr>
            ))}
            {detay.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>
                  Bu grupta sayım farkı bulunamadı.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SayimHatalariPage = () => {
  useAuth();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');

  const today = new Date();

  const [baslangic, setBaslangic] = useUrlState(
    'sayim_hata_baslangic',
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
  );

  const [bitis, setBitis] = useUrlState(
    'sayim_hata_bitis',
    today.toISOString().split('T')[0]
  );

  const [lokasyonId, setLokasyonId] = useUrlState('sayim_hata_lokasyon', '');

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let url =
        `${API_URL}/api/raporlar/sayim-hatalari` +
        `?tarih_baslangic=${baslangic}` +
        `&tarih_bitis=${bitis}`;

      if (lokasyonId) url += `&lokasyon_id=${lokasyonId}`;

      const res = await fetch(url, { headers: getAuthHeaders() });

      if (res.ok) {
        const result = await res.json();
        setData(result);
      } else {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || `Sayım hataları yüklenemedi (${res.status})`);
      }
    } catch (err) {
      logError('Sayım hataları yüklenemedi:', err);
      setError('Sunucu hatası — bağlantıyı kontrol edin');
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis, lokasyonId]);

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  const sayimHatasiDetay = data?.sayim_hatasi_detay || [];
  const sarfTuketimDetay = data?.sarf_tuketim_detay || [];

  const toplamKayit = Number(data?.kpis?.toplam_kayit || 0);
  const sayimHatasiKayit = Number(data?.kpis?.sayim_hatasi_kayit || 0);
  const sarfTuketimKayit = Number(data?.kpis?.sarf_tuketim_kayit || 0);

  const sayimHatasiMaliyet = Number(data?.kpis?.sayim_hatasi_maliyet || 0);
  const sarfTuketimMaliyet = Number(data?.kpis?.sarf_tuketim_maliyet || 0);
  const toplamMaliyet = Number(data?.kpis?.toplam_maliyet || 0);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  return (
    <div>
      <div style={{
        background: colors.surface,
        borderRadius: radius.lg,
        padding: '14px 16px',
        boxShadow: shadows.md,
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Başlangıç</div>
            <input
              type="date"
              value={baslangic}
              onChange={(e) => setBaslangic(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Bitiş</div>
            <input
              type="date"
              value={bitis}
              onChange={(e) => setBitis(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Lokasyon</div>
            <select
              value={lokasyonId}
              onChange={(e) => setLokasyonId(e.target.value)}
              style={{
                padding: '7px 9px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: 12,
                background: colors.surface,
                minWidth: 180,
              }}
            >
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map((lokasyon) => (
                <option key={lokasyon.id} value={lokasyon.id}>
                  {lokasyon.ad}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!loading && !error && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <OzetKarti
            baslik="Sayım Hatası"
            tutar={sayimHatasiMaliyet}
            kayit={sayimHatasiKayit}
          />
          <OzetKarti
            baslik="Sarf / Tüketim"
            tutar={sarfTuketimMaliyet}
            kayit={sarfTuketimKayit}
          />
          <OzetKarti
            baslik="Genel Toplam"
            tutar={toplamMaliyet}
            kayit={toplamKayit}
            vurgu
          />
        </div>
      )}

      {loading ? (
        <div style={{
          background: colors.surface,
          borderRadius: radius.lg,
          boxShadow: shadows.md,
          textAlign: 'center',
          padding: 50,
          color: '#9ca3af',
        }}>
          Yükleniyor...
        </div>
      ) : error ? (
        <div style={{
          background: colors.surface,
          borderRadius: radius.lg,
          boxShadow: shadows.md,
          textAlign: 'center',
          padding: 50,
          color: '#dc2626',
        }}>
          {error}
        </div>
      ) : (
        <>
          <RaporTablosu
            baslik="Sayım Hataları"
            aciklama="Sarf / tüketim ürünleri hariç"
            detay={sayimHatasiDetay}
            toplamMaliyet={sayimHatasiMaliyet}
            sortKey={sortKey}
            sortDirection={sortDirection}
            handleSort={handleSort}
          />

          <RaporTablosu
            baslik="Sarf / Tüketim Farkları"
            aciklama="Sarf listesinde tanımlı ürünler"
            detay={sarfTuketimDetay}
            toplamMaliyet={sarfTuketimMaliyet}
            sortKey={sortKey}
            sortDirection={sortDirection}
            handleSort={handleSort}
          />
        </>
      )}
    </div>
  );
};

export default SayimHatalariPage;
